import React, { useEffect, useState } from "react";
import { MdOutlineAccountBalanceWallet } from "react-icons/md";
import { RxLapTimer } from "react-icons/rx";
import { FaUserCheck, FaUserSlash } from "react-icons/fa";
import { USER_URL } from "../../constant/api";
import { Link } from "react-router-dom";
import ConfirmationModal from "../../components/AdminApproval/ConfirmationModal";
import { ScaleLoader } from "react-spinners";

function AdminSellerApproval() {
  const [filter, setFilter] = useState("All");
  const [products, setProducts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [actionType, setActionType] = useState("");
  const [loading, setLoading] = useState(true);

  // ✅ NEW: service fee state
  const [serviceFee, setServiceFee] = useState("");

  // Fetch sellers with filtering
  const fetchProducts = async () => {
    try {
      setLoading(true);

      const response = await USER_URL.get(`/accounts/admin/companies/`, {
        params: filter === "All" ? {} : { status: filter.toLowerCase() },
      });

      if (response.data?.data) {
        setProducts(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching products:", error.message || error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [filter]);

  // Open the confirmation modal
  const openModal = (product, action) => {
    setSelectedProduct(product);
    setActionType(action);

    // ✅ reset fee every time modal opens
    setServiceFee(product?.service_fee ?? "");

    setModalOpen(true);
  };

  // Handle seller status update
  const handleConfirm = async () => {
    if (!selectedProduct?.company_id) {
      console.error("Invalid product ID:", selectedProduct);
      return;
    }

    // ✅ Validate service_fee
    if (serviceFee === "" || serviceFee === null) {
      alert("Please enter service fee");
      return;
    }

    const feeNumber = Number(serviceFee);

    if (Number.isNaN(feeNumber) || feeNumber < 0) {
      alert("Service fee must be a valid number (0 or above)");
      return;
    }

    try {
      const newStatus = actionType === "Approve" ? "Approved" : "Rejected";

      await USER_URL.post(
        `/accounts/admin/companies/${selectedProduct.company_id}/`,
        {
          status: newStatus,
          service_fee: feeNumber,
        }
      );

      // ✅ Update UI immediately
      setProducts((prevProducts) =>
        prevProducts.map((product) =>
          product.company_id === selectedProduct.company_id
            ? { ...product, status: newStatus, service_fee: feeNumber }
            : product
        )
      );
    } catch (error) {
      console.error("Error updating status:", error.message || error);
    } finally {
      setModalOpen(false);
    }
  };

  // Filter products
  const filteredProducts =
    filter === "All"
      ? products
      : products.filter((product) => product.status === filter);

  return (
    <div className="container mx-auto">
      <h1 className="text-2xl font-medium text-green-600 ">Seller Approval</h1>

      {/* Tabs */}
      <div className="flex items-center justify-between border-b border-gray-300 w-full mb-5 mt-5">
        {["All", "Approved", "Rejected", "Pending"].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`flex flex-col items-center gap-1 ${
              filter === status ? "text-green-600" : "text-gray-400"
            }`}
          >
            <div className="flex items-center gap-2">
              {status === "All" && (
                <MdOutlineAccountBalanceWallet className="text-xl" />
              )}
              {status === "Approved" && <FaUserCheck className="text-xl" />}
              {status === "Rejected" && <FaUserSlash className="text-xl" />}
              {status === "Pending" && <RxLapTimer className="text-xl" />}
              <span className="font-semibold">{status}</span>
            </div>
            {filter === status && <div className="w-full h-1 bg-green-600"></div>}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center items-center h-96">
          <ScaleLoader color="#FFAE00" height={40} />
        </div>
      ) : (
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className="py-3 px-2 text-green-500 text-sm font-medium">
                  Company Name
                </th>
                <th className="py-3 px-2 text-green-500 text-sm font-medium">
                  Location
                </th>
                <th className="py-3 px-2 text-green-500 text-sm font-medium">
                  Brand
                </th>
                <th className="py-3 px-2 text-green-500 text-sm font-medium">
                  Join Date
                </th>
                <th className="py-3 px-2 text-green-500 text-sm font-medium">
                  Status
                </th>
                <th className="py-3 px-2 text-green-500 text-sm font-medium">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id} className="border-gray-300">
                  <td className="py-3 px-2">
                    <div className="rounded-lg border border-gray-300 px-4 py-2 text-gray-800 text-sm">
                      {product.name || "Company Name"}
                    </div>
                  </td>

                  <td className="py-3 px-2">
                    <div className="rounded-lg border border-gray-300 px-4 py-2 text-gray-800 text-sm">
                      {product.location || "Location"}
                    </div>
                  </td>

                  <td className="py-3 px-2">
                    <div className="rounded-lg border border-gray-300 px-4 py-2 text-gray-800 text-sm">
                      {product.brand || "Brand"}
                    </div>
                  </td>

                  <td className="py-3 px-2">
                    <div className="rounded-lg border border-gray-300 px-4 py-2 text-gray-800 text-sm">
                      {product.date_joined?.slice(0, 10) || "yyyy-mm-dd"}
                    </div>
                  </td>

                  <td className="py-3 px-2">
                    <div
                      className={`rounded-lg border px-4 py-2 text-sm ${
                        product.status === "Approved"
                          ? "text-green-600 border-green-600"
                          : product.status === "Rejected"
                          ? "text-red-600 border-red-600"
                          : "text-yellow-600 border-yellow-600"
                      }`}
                    >
                      {product.status || "Pending"}
                    </div>
                  </td>

                  <td className="py-3 px-2">
                    <button
                      onClick={() =>
                        openModal(
                          product,
                          product.status === "Approved" ? "Reject" : "Approve"
                        )
                      }
                      className={`${
                        product.status === "Approved"
                          ? "text-red-600 border-red-600 hover:bg-red-100"
                          : "text-green-600 border-green-600 hover:bg-green-100"
                      } border rounded-lg px-4 py-2 text-sm font-medium w-[100px]`}
                    >
                      {product.status === "Approved" ? "Reject" : "Approve"}
                    </button>

                    <Link
                      to={`/admin/approval/seller/view/${product.company_id}`}
                      className="text-green-600 border border-green-600 rounded-lg px-4 py-2 text-sm font-medium hover:bg-green-100 ml-5"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirm}
        action={actionType}
        product={selectedProduct}
        serviceFee={serviceFee}
        setServiceFee={setServiceFee}
      />
    </div>
  );
}

export default AdminSellerApproval;





// import React, { useEffect, useState } from "react";
// import { MdOutlineAccountBalanceWallet } from "react-icons/md";
// import { RxLapTimer } from "react-icons/rx";
// import { FaUserCheck, FaUserSlash } from "react-icons/fa";
// import axios from "axios";
// import { BASE_URL, USER_URL } from "../../constant/api";
// import { Link } from "react-router-dom";
// import ConfirmationModal from "../../components/AdminApproval/ConfirmationModal";
// import { ScaleLoader } from "react-spinners"; // Import a spinner from react-spinners

// function AdminSellerApproval() {
//   const [filter, setFilter] = useState("All");
//   const [products, setProducts] = useState([]);
//   const [modalOpen, setModalOpen] = useState(false);
//   const [selectedProduct, setSelectedProduct] = useState(null);
//   const [actionType, setActionType] = useState("");
//   const [loading, setLoading] = useState(true); // Add loading state

//   // Fetch sellers with filtering
//   const fetchProducts = async () => {
//     try {
//       setLoading(true); // Start loading
//       const response = await USER_URL.get(`/accounts/admin/companies/`, {
//         params: filter === "All" ? {} : { status: filter.toLowerCase() },
//       });

//       if (response.data?.data) {
//         setProducts(response.data.data);
//       }
//     } catch (error) {
//       console.error("Error fetching products:", error.message || error);
//     } finally {
//       setLoading(false); // Stop loading
//     }
//   };

//   // Fetch on component mount & on filter change
//   useEffect(() => {
//     fetchProducts();
//   }, [filter]);

//   // Open the confirmation modal
//   const openModal = (product, action) => {
//     setSelectedProduct(product);
//     setActionType(action);
//     setModalOpen(true);
//   };

//   // Handle seller status update
//   const handleConfirm = async () => {
//     if (selectedProduct && selectedProduct.company_id) {
//       try {
//         const newStatus = actionType === "Approve" ? "Approved" : "Rejected";
//         await USER_URL.post(`/accounts/admin/companies/${selectedProduct.company_id}/`, {
//           status: newStatus,
//         });

//         // Update UI
//         setProducts((prevProducts) =>
//           prevProducts.map((product) =>
//             product.company_id === selectedProduct.company_id
//               ? { ...product, status: newStatus }
//               : product
//           )
//         );
//       } catch (error) {
//         console.error("Error updating status:", error.message || error);
//       } finally {
//         setModalOpen(false);
//       }
//     } else {
//       console.error("Invalid product ID:", selectedProduct);
//     }
//   };

//   // Filter products based on selected filter
//   const filteredProducts =
//     filter === "All"
//       ? products
//       : products.filter((product) => product.status === filter);

//   return (
//     <div className="container mx-auto">
//       <h1 className="text-2xl font-medium text-green-600 ">Seller Approval</h1>
//       {/* Tabs Section */}
//       <div className="flex items-center justify-between border-b border-gray-300 w-full mb-5 mt-5">
//         {["All", "Approved", "Rejected", "Pending"].map((status) => (
//           <button
//             key={status}
//             onClick={() => setFilter(status)}
//             className={`flex flex-col items-center gap-1 ${
//               filter === status ? "text-green-600" : "text-gray-400"
//             }`}
//           >
//             <div className="flex items-center gap-2">
//               {status === "All" && <MdOutlineAccountBalanceWallet className="text-xl" />}
//               {status === "Approved" && <FaUserCheck className="text-xl" />}
//               {status === "Rejected" && <FaUserSlash className="text-xl" />}
//               {status === "Pending" && <RxLapTimer className="text-xl" />}
//               <span className="font-semibold">{status}</span>
//             </div>
//             {filter === status && (
//               <div className="w-full h-1 bg-green-600"></div>
//             )}
//           </button>
//         ))}
//       </div>

//       {/* Loading Animation */}
//       {loading ? (
//         <div className="flex justify-center items-center h-96">
//           <ScaleLoader color="#FFAE00" height={40} /> {/* Use ClipLoader from react-spinners */}
//         </div>
//       ) : (
//         /* Table Section */
//         <div className="overflow-x-auto mt-4">
//           <table className="w-full text-left">
//             <thead>
//               <tr>
//                 <th className="py-3 px-2 text-green-500 text-sm font-medium">
//                   Company Name
//                 </th>
//                 <th className="py-3 px-2 text-green-500 text-sm font-medium">
//                   Location
//                 </th>
//                 <th className="py-3 px-2 text-green-500 text-sm font-medium">
//                   Brand
//                 </th>
//                 <th className="py-3 px-2 text-green-500 text-sm font-medium">
//                   Join Date
//                 </th>
//                 <th className="py-3 px-2 text-green-500 text-sm font-medium">
//                   Status
//                 </th>
//                 <th className="py-3 px-2 text-green-500 text-sm font-medium">
//                   Actions
//                 </th>
//               </tr>
//             </thead>
//             <tbody>
//               {filteredProducts.map((product) => (
//                 <tr key={product.id} className="border-gray-300">
//                   <td className="py-3 px-2">
//                     <div className="rounded-lg border border-gray-300 px-4 py-2 text-gray-800 text-sm">
//                       {product.name || "Company Name"}
//                     </div>
//                   </td>
//                   <td className="py-3 px-2">
//                     <div className="rounded-lg border border-gray-300 px-4 py-2 text-gray-800 text-sm">
//                       {product.location || "Location"}
//                     </div>
//                   </td>
//                   <td className="py-3 px-2">
//                     <div className="rounded-lg border border-gray-300 px-4 py-2 text-gray-800 text-sm">
//                       {product.brand || "Brand"}
//                     </div>
//                   </td>
//                   <td className="py-3 px-2">
//                     <div className="rounded-lg border border-gray-300 px-4 py-2 text-gray-800 text-sm">
//                       {product.date_joined?.slice(0, 10) || "yyyy-mm-dd"}
//                     </div>
//                   </td>
//                   <td className="py-3 px-2">
//                     <div
//                       className={`rounded-lg border px-4 py-2 text-sm ${
//                         product.status === "Approved"
//                           ? "text-green-600 border-green-600"
//                           : product.status === "Rejected"
//                           ? "text-red-600 border-red-600"
//                           : "text-yellow-600 border-yellow-600"
//                       }`}
//                     >
//                       {product.status || "Pending"}
//                     </div>
//                   </td>
//                   <td className="py-3 px-2">
//                     <button
//                       onClick={() =>
//                         openModal(
//                           product,
//                           product.status === "Approved" ? "Reject" : "Approve"
//                         )
//                       }
//                       className={`${
//                         product.status === "Approved"
//                           ? "text-red-600 border-red-600 hover:bg-red-100"
//                           : "text-green-600 border-green-600 hover:bg-green-100"
//                       } border rounded-lg px-4 py-2 text-sm font-medium w-[100px]`}
//                     >
//                       {product.status === "Approved" ? "Reject" : "Approve"}
//                     </button>
//                     <Link
//                       to={`/admin/approval/seller/view/${product.company_id}`}
//                       className="text-green-600 border border-green-600 rounded-lg px-4 py-2 text-sm font-medium hover:bg-green-100 ml-5"
//                     >
//                       View
//                     </Link>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       )}

//       {/* Confirmation Modal */}
//       <ConfirmationModal
//         isOpen={modalOpen}
//         onClose={() => setModalOpen(false)}
//         onConfirm={handleConfirm}
//         action={actionType}
//         product={selectedProduct}
//       />
//     </div>
//   );
// }

// export default AdminSellerApproval;